import React, { useEffect, useState } from "react";
import { analyticsApi } from "@/lib/api";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, ReferenceLine } from "recharts";
import { TrendingUp, TrendingDown, Target, Award, Flame } from "lucide-react";

const CHART_MARGIN = { top: 8, right: 12, left: -20, bottom: 0 };
const AXIS_TICK = { fill: "#64748B", fontSize: 10, fontFamily: "JetBrains Mono" };
const TOOLTIP_CONTENT_STYLE = {
  background: "#12141D",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: 8,
  fontFamily: "JetBrains Mono",
  fontSize: 12,
};
const TOOLTIP_LABEL_STYLE = { color: "#94A3B8" };
const tooltipFormatter = (v) => [`$${Number(v).toFixed(2)}`, "P&L"];

function Stat({ label, value, tone = "default", testid }) {
  const cls =
    tone === "positive" ? "text-emerald-300" :
    tone === "negative" ? "text-rose-300" :
    "text-slate-100";
  return (
    <div className="rounded-lg border border-white/5 bg-black/30 p-3">
      <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-slate-500">{label}</div>
      <div data-testid={testid} className={`mt-1 font-display text-xl font-bold ${cls}`}>{value}</div>
    </div>
  );
}

function money(v, withSign = false) {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  const sign = withSign && n > 0 ? "+" : "";
  return `${sign}$${n.toFixed(2)}`;
}

export default function ProfitAnalytics() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let mounted = true;
    analyticsApi.summary()
      .then((r) => { if (mounted) setData(r); })
      .catch((e) => { console.debug("[Analytics] fetch failed", e); if (mounted) setErr("Analytics unavailable"); });
    return () => { mounted = false; };
  }, []);

  if (err) {
    return (
      <div data-testid="profit-analytics" className="aq-card p-4 text-slate-400 text-sm">
        {err}
      </div>
    );
  }

  if (!data) {
    return (
      <div data-testid="profit-analytics" className="aq-card p-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Analytics</div>
        <div className="mt-1 font-display text-lg font-bold">Profit analytics</div>
        <div className="mt-6 text-center text-slate-500 text-xs font-mono">Loading…</div>
      </div>
    );
  }

  const netPositive = (data.total_pnl || 0) >= 0;

  return (
    <div data-testid="profit-analytics" className="aq-card p-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Analytics</div>
          <div className="mt-0.5 font-display text-lg font-bold">Profit analytics</div>
        </div>
        <div className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full border ${
          netPositive ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" :
          "border-rose-500/40 bg-rose-500/10 text-rose-300"
        }`}>
          {netPositive ? "green" : "red"}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
        <Stat
          label="Net P&L"
          value={money(data.total_pnl, true)}
          tone={netPositive ? "positive" : "negative"}
          testid="analytics-net-pnl"
        />
        <Stat label="Win rate" value={`${data.win_rate_pct}%`} testid="analytics-win-rate" />
        <Stat label="Closes" value={data.closed_trades} testid="analytics-closed" />
        <Stat label="Fees paid" value={money(data.total_fees_paid)} testid="analytics-fees" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
        <Stat label="Avg win" value={money(data.avg_win, true)} tone="positive" testid="analytics-avg-win" />
        <Stat label="Avg loss" value={money(data.avg_loss, true)} tone="negative" testid="analytics-avg-loss" />
        <Stat
          label="Profit factor"
          value={data.profit_factor !== null ? data.profit_factor : "—"}
          testid="analytics-profit-factor"
        />
      </div>

      {/* Daily P&L chart */}
      <div className="mt-4 rounded-lg border border-white/5 bg-black/30 p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Daily P&L · last 30 days</div>
          <div className="font-mono text-[10px] text-slate-500">{data.daily_pnl.length} sessions</div>
        </div>
        <div className="h-40" data-testid="analytics-daily-chart">
          {data.daily_pnl.length === 0 ? (
            <div className="h-full grid place-items-center text-slate-500 text-xs font-mono">
              No closed trades yet — the chart will populate as your bot books wins.
            </div>
          ) : (
            <ResponsiveContainer>
              <LineChart data={data.daily_pnl} margin={CHART_MARGIN}>
                <XAxis dataKey="date" tick={AXIS_TICK} tickFormatter={(d) => d.slice(5)} axisLine={false} tickLine={false} minTickGap={30} />
                <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} />
                <ReferenceLine y={0} stroke="rgba(255,255,255,0.15)" strokeDasharray="3 3" />
                <Tooltip
                  contentStyle={TOOLTIP_CONTENT_STYLE}
                  labelStyle={TOOLTIP_LABEL_STYLE}
                  formatter={tooltipFormatter}
                />
                <Line type="monotone" dataKey="pnl" stroke="#34D399" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Best / worst */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
        <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3">
          <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-emerald-300">
            <Award className="w-3 h-3" /> best trade
          </div>
          {data.best_trade ? (
            <div className="mt-1.5">
              <div className="font-display font-bold text-slate-100">
                {data.best_trade.symbol} · <span className="text-emerald-300">{money(data.best_trade.realized_pnl, true)}</span>
              </div>
              <div className="text-[11px] font-mono text-slate-500 mt-0.5">
                {data.best_trade.side} {data.best_trade.quantity} @ ${data.best_trade.price}
              </div>
            </div>
          ) : (
            <div className="mt-1 text-xs text-slate-500 font-mono">Not yet</div>
          )}
        </div>
        <div className="rounded-lg border border-rose-500/20 bg-rose-500/5 p-3">
          <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-rose-300">
            <Flame className="w-3 h-3" /> worst trade
          </div>
          {data.worst_trade && data.worst_trade.realized_pnl !== null ? (
            <div className="mt-1.5">
              <div className="font-display font-bold text-slate-100">
                {data.worst_trade.symbol} · <span className="text-rose-300">{money(data.worst_trade.realized_pnl, true)}</span>
              </div>
              <div className="text-[11px] font-mono text-slate-500 mt-0.5">
                {data.worst_trade.side} {data.worst_trade.quantity} @ ${data.worst_trade.price}
              </div>
            </div>
          ) : (
            <div className="mt-1 text-xs text-slate-500 font-mono">No losses yet — keep it up!</div>
          )}
        </div>
      </div>
    </div>
  );
}
